import { Tooltip as TooltipPrimitive } from 'radix-ui'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

function TooltipProvider({ delayDuration = 300, ...props }: ComponentProps<typeof TooltipPrimitive.Provider>) {
    return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />
}

const Tooltip = TooltipPrimitive.Root

/**
 * Radix opens a tooltip on *any* focus, and guards only against focus that directly
 * follows a pointerdown on the same trigger. Focus a dialog moves gets through that
 * guard: opening a sheet sends focus to the first tabbable thing inside it, and
 * closing one sends it back to the button that opened it. Since `CopyableId`,
 * `DateTime`, and `StatusBadge` are all tooltip triggers, that focus almost always
 * lands on one, and a tooltip appears somewhere the cursor never went -- then stays,
 * because the provider only closes it when another trigger takes the shared slot.
 *
 * A tooltip on focus exists for keyboard users, so defer to the browser's own answer
 * to "did the keyboard do this" and suppress the rest. Radix composes the caller's
 * handler ahead of its own and skips its own when the event is defaulted, so
 * `preventDefault` here is the documented way to opt out of the open.
 */
function TooltipTrigger({ onFocus, ...props }: ComponentProps<typeof TooltipPrimitive.Trigger>) {
    return (
        <TooltipPrimitive.Trigger
            {...props}
            onFocus={event => {
                onFocus?.(event)
                if (event.defaultPrevented) return
                if (!event.currentTarget.matches(':focus-visible')) event.preventDefault()
            }}
        />
    )
}

function TooltipContent({
    className,
    sideOffset = 4,
    children,
    ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                className={cn(
                    'z-50 w-fit max-w-xs rounded-md border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md',
                    'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    className
                )}
                {...props}>
                {children}
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
